<template>
  <b-modal
      :visible="visible"
      ref="modal"
      id="admin-category-edit-modal"
      title="Edit Category"
      @hidden="$emit('cancel')"
      hide-footer
      centered
  >
    <b-form @submit.prevent="submitForm">

      <b-form-group label="Category Name">
        <b-form-input
            v-model.trim="formData.name"
            type="text"
            :state="fieldValid('name')"
        />
        <b-form-invalid-feedback :state="fieldValid('name')">
          {{ formValidation.name }}
        </b-form-invalid-feedback>
      </b-form-group>

      <b-form-group label="Category Type">
        <b-form-select
            v-model="formData.category_type"
            :options="categoryTypes"
            :state="fieldValid('category_type')"
        />
        <b-form-invalid-feedback :state="fieldValid('category_type')">
          {{ formValidation.category_type }}
        </b-form-invalid-feedback>
      </b-form-group>

      <b-form-group label="Bulk or Packaged for Retail">
        <b-form-select
            v-model="formData.bulk_or_packaged"
            :options="bulkOrPackagedOptions"
            :state="fieldValid('bulk_or_packaged')"
        />
        <b-form-invalid-feedback :state="fieldValid('bulk_or_packaged')">
          {{ formValidation.bulk_or_packaged }}
        </b-form-invalid-feedback>
      </b-form-group>

      <b-form-group v-if="isBulk" label="Bulk Units of Measure">
        <b-form-checkbox-group
            v-model="formData.bulk_unit_types"
            :state="fieldValid('bulk_unit_types')"
        >
          <b-form-checkbox
              v-for="(option, key) in unitTypesOptions"
              :key="key"
              :value="option.value"
          >
            {{ option.text }}
          </b-form-checkbox>
        </b-form-checkbox-group>
        <b-form-invalid-feedback :state="fieldValid('bulk_unit_types')">
          {{ formValidation.bulk_unit_types }}
        </b-form-invalid-feedback>
      </b-form-group>

      <b-form-group v-if="isPackaged" label="Packaged Units of Measure">
        <b-form-checkbox-group
            v-model="formData.packaged_unit_types"
            :state="fieldValid('packaged_unit_types')"
        >
          <b-form-checkbox
              v-for="(option, key) in unitTypesOptions"
              :key="key"
              :value="option.value"
          >
            {{ option.text }}
          </b-form-checkbox>
        </b-form-checkbox-group>
        <b-form-invalid-feedback :state="fieldValid('packaged_unit_types')">
          {{ formValidation.packaged_unit_types }}
        </b-form-invalid-feedback>
      </b-form-group>

      <b-form-group label="THC/CBD Units">
        <b-form-checkbox-group
            v-model="formData.thc_cbd_options"
            :state="fieldValid('thc_cbd_options')"
        >
          <b-form-checkbox
              v-for="(option, key) in thcCbdOptions"
              :key="key"
              :value="option.value"
          >
            {{ option.text }}
          </b-form-checkbox>
        </b-form-checkbox-group>
        <b-form-invalid-feedback :state="fieldValid('thc_cbd_options')">
          {{ formValidation.thc_cbd_options }}
        </b-form-invalid-feedback>
      </b-form-group>

      <b-button type="submit" variant="success" :disabled="formState.processing">
        Submit
        <b-spinner v-if="formState.processing" class="ml-1" small></b-spinner>
      </b-button>
      <b-btn type="button" variant="secondary" @click.prevent="close">Cancel</b-btn>
    </b-form>
  </b-modal>
</template>

<script>
import {API_ROUTES} from "../api";
import {MEASURE_UNIT_TYPES_OPTIONS} from "../constants";

export default {
  name: "AdminCategoryEditModal",
  emits: ['success', 'cancel'],
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    category: {
      type: Object,
      require: true
    }
  },
  data() {
    return {
      formData: {
        name: this.category.name,
        category_type: this.category.category_type_id,
        bulk_or_packaged: this.category.bulk_or_packaged,
        bulk_unit_types: this.category.bulk_unit_types,
        packaged_unit_types: this.category.packaged_unit_types,
        thc_cbd_options: this.category.thc_cbd_options,
      },
      formValidation: {},
      formState: {
        processing: false
      },
      bulkOrPackagedOptions: [
        {
          text: "Both",
          value: "both",
        },
        {
          text: "Packaged",
          value: "packaged",
        },
        {
          text: "Bulk",
          value: "bulk",
        },
      ],
      thcCbdOptions: [
        {
          text: "%",
          value: "%",
        },
        {
          text: "% range",
          value: "%_range",
        },
        {
          text: "mg",
          value: "mg",
        }
      ],
      unitTypesOptions: MEASURE_UNIT_TYPES_OPTIONS
    }
  },
  computed: {
    categoryTypes() {
      return this.$store.state.data.category_types.map(ct => ({
        value: ct.id,
        text: ct.name
      }));
    },
    isBulk() {
      return this.formData.bulk_or_packaged === 'bulk' || this.formData.bulk_or_packaged === 'both';
    },
    isPackaged() {
      return this.formData.bulk_or_packaged === 'packaged' || this.formData.bulk_or_packaged === 'both';
    }
  },
  methods: {
    open() {
      this.$root.$emit('bv::show::modal', 'admin-category-edit-modal');
    },
    close() {
      this.$root.$emit('bv::hide::modal', 'admin-category-edit-modal');
    },
    fieldValid(field) {
      return typeof this.formValidation[field] !== "undefined" && this.formValidation[field].length > 0 ? false : null;
    },
    submitForm() {
      this.formState.processing = true;
      this.$api.post(`${API_ROUTES.ADMIN_TAXONOMY_CATEGORIES}/${this.category.id}`, this.formData).then(({data}) => {
        this.formValidation = {};
        this.$emit('success', data.category);
        this.close();
      }).catch(({response: {data}}) => {
        this.$bvToast.toast(data.message ?? 'Failed to create a new category', {
          title: 'Error!',
          variant: 'danger'
        });
        let validationErrors = {};
        if (typeof data.errors !== 'undefined') {
          for (let fieldName in data.errors) {
            validationErrors[fieldName] = data.errors[fieldName].join(', ');
          }
        }
        this.formValidation = validationErrors;
      }).finally(() => {
        this.formState.processing = false;
      });
    }
  }
}
</script>
